<template>
  <!-- FT-191 REQ-03: PIN для общих компьютеров — в профиле и в диалоге после входа. -->
  <form data-test="pin-form" class="pin-form" @submit.prevent="save">
    <div class="pin-form__row">
      <v-text-field
        v-model="pin"
        data-test="pin-new"
        type="text"
        class="pin-form__secret"
        inputmode="numeric"
        autocomplete="off"
        data-lpignore="true"
        data-1p-ignore
        data-bwignore="true"
        data-form-type="other"
        maxlength="4"
        outlined
        dense
        prepend-inner-icon="mdi-dialpad"
        :label="$t('pin_settings.new')"
        :hint="$t('pin_settings.hint')"
        persistent-hint
        @input="pin = digitsOnly(pin)"
      />
      <v-text-field
        v-model="pinConfirmation"
        data-test="pin-confirmation"
        type="text"
        class="pin-form__secret"
        inputmode="numeric"
        autocomplete="off"
        data-lpignore="true"
        data-1p-ignore
        data-bwignore="true"
        data-form-type="other"
        maxlength="4"
        outlined
        dense
        prepend-inner-icon="mdi-dialpad"
        :label="$t('pin_settings.confirmation')"
        @input="pinConfirmation = digitsOnly(pinConfirmation)"
      />
    </div>
    <v-expand-transition>
      <v-text-field
        v-if="passwordRequired"
        v-model="currentPassword"
        data-test="pin-password"
        type="password"
        autocomplete="current-password"
        outlined
        dense
        prepend-inner-icon="mdi-lock-outline"
        class="mt-1"
        :label="$t('pin_settings.current_password')"
      />
    </v-expand-transition>
    <v-alert v-if="errorKey" type="error" dense text class="mb-3" data-test="pin-error">{{ $t(errorKey) }}</v-alert>
    <div class="pin-form__actions">
      <v-btn type="submit" color="success" block large depressed :loading="loading" data-test="pin-save">{{ $t("pin_settings.save") }}</v-btn>
      <v-btn v-if="allowReset" text block color="error" :disabled="loading" data-test="pin-reset" @click="reset">
        {{ $t("pin_settings.reset") }}
      </v-btn>
    </div>
  </form>
</template>

<script>
import { PIN_FORMAT } from "@/utils/deviceLock";

export default {
  name: "PinForm",
  props: {
    // Профиль не знает, задан ли PIN (pin_set приходит только в ответе входа) — сброс показываем всегда;
    // поле пароля появляется, когда сервер ответил reauth_required (смена заданного PIN — REQ-03).
    allowReset: { type: Boolean, default: false },
  },
  data() {
    return {
      pin: "",
      pinConfirmation: "",
      currentPassword: "",
      reauthRequired: false,
      loading: false,
      errorKey: null,
    };
  },
  computed: {
    passwordRequired() {
      return this.reauthRequired;
    },
    url() {
      return `${this.$store.getters.getApiUrl}/api/v1/account/pin`;
    },
  },
  methods: {
    digitsOnly(value) {
      return String(value || "").replace(/\D/g, "").slice(0, 4);
    },
    async save() {
      if (this.loading) return; // повторный Enter во время запроса не шлёт второй PUT
      this.errorKey = null;
      if (!PIN_FORMAT.test(this.pin)) {
        this.errorKey = "pin_settings.invalid_format";
        return;
      }
      if (this.pin !== this.pinConfirmation) {
        this.errorKey = "pin_settings.mismatch";
        return;
      }
      if (this.passwordRequired && !this.currentPassword) {
        this.errorKey = "pin_settings.password_required";
        return;
      }
      const body = { pin: this.pin };
      if (this.currentPassword) body.current_password = this.currentPassword;
      this.loading = true;
      try {
        await this.axios.put(this.url, body);
        this.pin = "";
        this.pinConfirmation = "";
        this.currentPassword = "";
        this.$emit("saved");
      } catch (error) {
        const data = error && error.response && error.response.data;
        const code = data && data.error && data.error.code;
        if (code === "reauth_required") {
          this.reauthRequired = true;
          this.errorKey = "pin_settings.reauth_required";
        } else if (code === "invalid_pin_format") {
          this.errorKey = "pin_settings.invalid_format";
        } else {
          this.errorKey = "pin_settings.save_failed";
        }
      } finally {
        this.loading = false;
      }
    },
    async reset() {
      this.loading = true;
      try {
        await this.axios.delete(this.url);
        this.$emit("reset");
      } catch (error) {
        this.errorKey = "pin_settings.save_failed";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pin-form__row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0 12px;
}

.pin-form__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

// type=text, а не password: иначе браузер и менеджеры паролей предлагают сохранить и подставить
// пароль. Символы скрыты так же, как в поле пароля.
.pin-form__secret ::v-deep input {
  -webkit-text-security: disc;
}
</style>
